import { useState, useEffect, useCallback } from 'react';
import { teamChatService } from '@/services/teamChatService';
import { ChatGroup } from '@/types/chat';
import { useUnifiedUser } from './useUnifiedUser';
import { toast } from 'sonner';

export const useTeamChat = () => {
  const { user: unifiedUser } = useUnifiedUser();
  const [teamChats, setTeamChats] = useState<ChatGroup[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTeamChats = useCallback(async () => {
    if (!unifiedUser?.id) return;

    try {
      setLoading(true);
      const chats = await teamChatService.getTeamChats(unifiedUser.id);
      setTeamChats(chats);
    } catch (error) {
      console.error('Error loading team chats:', error);
      toast.error('Failed to load team chats');
    } finally {
      setLoading(false);
    }
  }, [unifiedUser?.id]);

  const createTeamChat = useCallback(async (teamId: number): Promise<ChatGroup | null> => {
    if (!unifiedUser?.id) return null;

    try {
      const chat = await teamChatService.createTeamChat(teamId, unifiedUser.id);
      if (chat) {
        await loadTeamChats(); // Refresh the list
        toast.success(`Team chat created for team!`);
      }
      return chat;
    } catch (error) {
      console.error('Error creating team chat:', error);
      toast.error('Failed to create team chat');
      return null;
    }
  }, [unifiedUser?.id, loadTeamChats]);

  const joinTeamChatAsParent = useCallback(async (chatGroupId: string): Promise<boolean> => {
    if (!unifiedUser?.id) return false;

    try {
      const success = await teamChatService.joinTeamChatAsParent(unifiedUser.id, chatGroupId);
      if (success) {
        await loadTeamChats(); // Refresh the list
        toast.success('Joined team chat as parent');
      }
      return success;
    } catch (error) {
      console.error('Error joining team chat as parent:', error);
      toast.error('Failed to join team chat');
      return false;
    }
  }, [unifiedUser?.id, loadTeamChats]);

  const canAccessTeamChat = useCallback(async (chatGroupId: string): Promise<boolean> => {
    if (!unifiedUser?.id) return false;

    try {
      return await teamChatService.canAccessTeamChat(unifiedUser.id, chatGroupId);
    } catch (error) {
      console.error('Error checking team chat access:', error);
      return false;
    }
  }, [unifiedUser?.id]);

  useEffect(() => {
    loadTeamChats();
  }, [loadTeamChats]);

  return {
    teamChats,
    loading,
    loadTeamChats,
    createTeamChat,
    joinTeamChatAsParent,
    canAccessTeamChat
  };
};